<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import signInImage from '@/images/account/sign-in.jpg';
import { AuthRouteName } from '@/js/account/app/router/route-names';
import AuthTitle from '@/js/account/components/auth/AuthTitle.vue';
import BaseButton from '@/js/account/components/base/BaseButton.vue';
import BaseIcon from '@/js/account/components/base/BaseIcon.vue';
import AuthLayout from '@/js/account/layouts/auth-layout/AuthLayout.vue';
import { classNames } from '@/js/lib/bem';

type CheckMailContent = {
  title: string
  lines: [string, string]
  showSignIn?: boolean
};

const CHECK_MAIL_CONTENT: Record<string, CheckMailContent> = {
  [AuthRouteName.VerifyEmail]: {
    title: 'Check your email',
    lines: [
      'Account activation link has been sent',
      'to the email address you provided',
    ],
  },
  [AuthRouteName.CheckMail]: {
    title: 'Check your mail',
    lines: ['We have sent password recovery instructions', 'to your email'],
  },
  [AuthRouteName.EmailChanged]: {
    title: 'Check your email',
    lines: [
      'A confirmation link has been sent to your new email address',
      'Please confirm it and sign in again',
    ],
    showSignIn: true,
  },
};

const route = useRoute();
const router = useRouter();
const cn = classNames('check-your-email-page');

const content = computed((): CheckMailContent => {
  const name = route.name;

  if (typeof name === 'string' && name in CHECK_MAIL_CONTENT) {
    return CHECK_MAIL_CONTENT[name];
  }

  return CHECK_MAIL_CONTENT[AuthRouteName.VerifyEmail];
});

function goToSignIn(): void {
  void router.push({ name: AuthRouteName.Login });
}
</script>

<template>
  <AuthLayout :src="signInImage" alt="">
    <div :class="cn()">
      <BaseIcon
        type="main"
        name="mail-opened"
        :class="cn('icon')"
        :width="100"
        :height="100"
      />
      <AuthTitle :class="cn('title')" :title="content.title">
        <template #description>
          <p :class="cn('description')">
            {{ content.lines[0] }}<br>
            {{ content.lines[1] }}
          </p>
        </template>
      </AuthTitle>
      <BaseButton
        v-if="content.showSignIn"
        :class="cn('button')"
        type="button"
        full-width
        @click="goToSignIn"
      >
        Sign in
      </BaseButton>
    </div>
  </AuthLayout>
</template>

<style lang="scss" scoped>
.check-your-email-page {
  max-width: 363px;
  width: 100%;
  text-align: center;

  &__icon {
    display: block;
    margin: 0 auto 40px;
  }

  &__title {
    text-align: center;
    margin: 0;
  }

  &__button {
    margin-top: 40px;
  }

  &__description {
    margin: 0;
    font-family: var(--font-family-sans);
    font-weight: var(--font-weight-regular);
    font-size: 16px;
    line-height: 1.375;
    color: var(--color-text-secondary);
  }
}
</style>
